import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  Alert,
  Paper,
  CircularProgress,
  Divider,
  alpha,
  useTheme,
  Autocomplete,
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  Business as BusinessIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'react-toastify';
import { fixedAssetsService } from '../../services/fixedAssets.service';
import {
  FixedAsset,
  FixedAssetCategory,
  CreateFixedAssetDto,
  UpdateFixedAssetDto,
  AssetType,
  DepreciationMethod,
} from '../../types/fixedAssets.types';
import { branchesService } from '../../services/branches.service';
import { Branch } from '../../types/branches.types';
import { costCentersService, CostCenterDto } from '../../services/costCenters.service';
import AccountSelector from '../common/AccountSelector';

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  editAsset?: FixedAsset | null;
}

interface FormState {
  assetName: string;
  assetNameAr: string;
  assetType: AssetType;
  categoryId: string;
  branchId: string;
  notes: string;
  acquisitionDate: string;
  acquisitionCost: string;
  residualValue: string;
  usefulLifeYears: string;
  depreciationMethod: DepreciationMethod;
  decliningRate: string;
  assetAccountId: number | null;
  accumDepAccountId: number | null;
  depExpenseAccountId: number | null;
  costCenterId: string;
}

const emptyForm = (): FormState => ({
  assetName: '',
  assetNameAr: '',
  assetType: 'Tangible',
  categoryId: '',
  branchId: '',
  notes: '',
  acquisitionDate: new Date().toISOString().split('T')[0],
  acquisitionCost: '',
  residualValue: '0',
  usefulLifeYears: '',
  depreciationMethod: 'StraightLine',
  decliningRate: '',
  assetAccountId: null,
  accumDepAccountId: null,
  depExpenseAccountId: null,
  costCenterId: '',
});

const FixedAssetDialog: React.FC<Props> = ({ open, onClose, onSave, editAsset }) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const isEdit = !!editAsset;
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [form, setForm] = useState<FormState>(emptyForm());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState<FixedAssetCategory[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [costCenters, setCostCenters] = useState<CostCenterDto[]>([]);
  const [loadingMeta, setLoadingMeta] = useState(false);

  // ── Load reference data ────────────────────────────────────────────────────
  const loadMeta = useCallback(async () => {
    setLoadingMeta(true);
    try {
      const [cats, brs, ccs] = await Promise.all([
        fixedAssetsService.getAllCategories(),
        branchesService.getAll(),
        costCentersService.getAll(false, true),
      ]);
      setCategories(cats.filter(c => c.isActive));
      setBranches(brs.filter(b => b.isActive !== false && b.branchType !== 'Warehouse'));
      setCostCenters(ccs);
    } catch {
      // non-fatal
    } finally {
      setLoadingMeta(false);
    }
  }, []);

  // ── Populate form on open ──────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    loadMeta();
    if (editAsset) {
      setForm({
        assetName: editAsset.assetName,
        assetNameAr: editAsset.assetNameAr,
        assetType: editAsset.assetType,
        categoryId: editAsset.categoryId != null ? String(editAsset.categoryId) : '',
        branchId: editAsset.branchId != null ? String(editAsset.branchId) : '',
        notes: editAsset.notes ?? '',
        acquisitionDate: editAsset.acquisitionDate.split('T')[0],
        acquisitionCost: String(editAsset.acquisitionCost),
        residualValue: String(editAsset.residualValue),
        usefulLifeYears: String(editAsset.usefulLifeYears),
        depreciationMethod: editAsset.depreciationMethod,
        decliningRate:
          editAsset.decliningRate != null
            ? String(editAsset.decliningRate * 100)
            : '',
        assetAccountId: editAsset.assetAccountId ?? null,
        accumDepAccountId: editAsset.accumDepAccountId ?? null,
        depExpenseAccountId: editAsset.depExpenseAccountId ?? null,
        costCenterId:
          editAsset.costCenterId != null ? String(editAsset.costCenterId) : '',
      });
    } else {
      setForm(emptyForm());
    }
    setError('');
  }, [open, editAsset]);

  // ── Auto-fill from category (new assets only) ──────────────────────────────
  useEffect(() => {
    if (!form.categoryId || isEdit) return;
    const cat = categories.find(c => String(c.categoryId) === form.categoryId);
    if (!cat) return;
    setForm(prev => ({
      ...prev,
      assetType: cat.assetType,
      usefulLifeYears:
        prev.usefulLifeYears === '' && cat.defaultUsefulLifeYears != null
          ? String(cat.defaultUsefulLifeYears)
          : prev.usefulLifeYears,
      depreciationMethod: cat.defaultDepreciationMethod ?? prev.depreciationMethod,
      decliningRate:
        prev.decliningRate === '' && cat.defaultDecliningRate != null
          ? String(cat.defaultDecliningRate * 100)
          : prev.decliningRate,
      assetAccountId: prev.assetAccountId ?? cat.defaultAssetAccountId ?? null,
      accumDepAccountId: prev.accumDepAccountId ?? cat.defaultAccumDepAccountId ?? null,
      depExpenseAccountId:
        prev.depExpenseAccountId ?? cat.defaultDepExpenseAccountId ?? null,
    }));
  }, [form.categoryId, categories, isEdit]);

  // ── Field setter helper ────────────────────────────────────────────────────
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  // ── Depreciation preview ───────────────────────────────────────────────────
  const preview = (() => {
    const cost = parseFloat(form.acquisitionCost);
    const residual = parseFloat(form.residualValue);
    const years = parseFloat(form.usefulLifeYears);
    if (isNaN(cost) || isNaN(residual) || isNaN(years) || years <= 0 || cost <= 0)
      return null;

    if (form.depreciationMethod === 'StraightLine') {
      const annual = (cost - residual) / years;
      return {
        annual,
        monthly: annual / 12,
        label: isRTL ? 'القسط الثابت' : 'Straight Line',
      };
    } else {
      const rate = parseFloat(form.decliningRate);
      if (isNaN(rate) || rate <= 0) return null;
      const annual = cost * (rate / 100);
      return {
        annual,
        monthly: annual / 12,
        label: isRTL ? 'القسط المتناقص' : 'Declining Balance',
      };
    }
  })();

  // ── Validation ─────────────────────────────────────────────────────────────
  const validate = (): boolean => {
    if (!form.assetName.trim()) {
      setError(isRTL ? 'اسم الأصل (إنجليزي) مطلوب' : 'Asset name (EN) is required');
      return false;
    }
    if (!form.assetNameAr.trim()) {
      setError(isRTL ? 'اسم الأصل (عربي) مطلوب' : 'Asset name (AR) is required');
      return false;
    }
    if (!form.acquisitionDate) {
      setError(isRTL ? 'تاريخ الاستحواذ مطلوب' : 'Acquisition date is required');
      return false;
    }
    const cost = parseFloat(form.acquisitionCost);
    if (isNaN(cost) || cost <= 0) {
      setError(isRTL ? 'تكلفة الاستحواذ مطلوبة ويجب أن تكون أكبر من صفر' : 'Acquisition cost is required and must be > 0');
      return false;
    }
    const years = parseFloat(form.usefulLifeYears);
    if (isNaN(years) || years <= 0) {
      setError(isRTL ? 'العمر الإنتاجي مطلوب' : 'Useful life (years) is required');
      return false;
    }
    if (
      form.depreciationMethod === 'DecliningBalance' &&
      (isNaN(parseFloat(form.decliningRate)) || parseFloat(form.decliningRate) <= 0)
    ) {
      setError(
        isRTL
          ? 'نسبة الاستهلاك المتناقص مطلوبة'
          : 'Declining rate is required for Declining Balance method'
      );
      return false;
    }
    return true;
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setError('');
    if (!validate()) return;

    const payload: CreateFixedAssetDto = {
      assetName: form.assetName.trim(),
      assetNameAr: form.assetNameAr.trim(),
      assetType: form.assetType,
      categoryId: form.categoryId ? Number(form.categoryId) : undefined,
      branchId: form.branchId ? Number(form.branchId) : undefined,
      notes: form.notes.trim() || undefined,
      acquisitionDate: form.acquisitionDate,
      acquisitionCost: parseFloat(form.acquisitionCost),
      residualValue: parseFloat(form.residualValue) || 0,
      usefulLifeYears: parseFloat(form.usefulLifeYears),
      depreciationMethod: form.depreciationMethod,
      decliningRate:
        form.depreciationMethod === 'DecliningBalance' && form.decliningRate
          ? parseFloat(form.decliningRate) / 100
          : undefined,
      assetAccountId: form.assetAccountId ?? undefined,
      accumDepAccountId: form.accumDepAccountId ?? undefined,
      depExpenseAccountId: form.depExpenseAccountId ?? undefined,
      costCenterId: form.costCenterId ? Number(form.costCenterId) : undefined,
    };

    try {
      setLoading(true);
      if (isEdit && editAsset) {
        const updatePayload: UpdateFixedAssetDto = {
          ...payload,
          fixedAssetId: editAsset.fixedAssetId,
        };
        await fixedAssetsService.update(editAsset.fixedAssetId, updatePayload);
        toast.success(isRTL ? 'تم تحديث الأصل بنجاح' : 'Asset updated successfully');
      } else {
        await fixedAssetsService.create(payload);
        toast.success(isRTL ? 'تم إنشاء الأصل بنجاح' : 'Asset created successfully');
      }
      onSave();
      onClose();
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        (isEdit
          ? isRTL
            ? 'فشل تحديث الأصل'
            : 'Failed to update asset'
          : isRTL
          ? 'فشل إنشاء الأصل'
          : 'Failed to create asset');
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const getCategoryLabel = (cat: FixedAssetCategory) =>
    isRTL ? cat.categoryNameAr : cat.categoryName;

  const getBranchLabel = (b: Branch) =>
    isRTL ? b.branchNameAr : b.branchName;

  // ──────────────────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      dir={isRTL ? 'rtl' : 'ltr'}
      PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh', bgcolor: 'background.paper' } }}
    >
      {/* ── Gradient Header ──────────────────────────────────────────────── */}
      <DialogTitle
        sx={{
          background: isDark
            ? 'linear-gradient(135deg, #0d2f6e 0%, #1565c0 50%, #1976d2 100%)'
            : 'linear-gradient(135deg, #1565c0 0%, #1976d2 50%, #42a5f5 100%)',
          color: 'white',
          py: 2.5,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <AccountBalanceIcon sx={{ fontSize: 28 }} />
        <Box>
          <Typography variant="h6" fontWeight="bold" lineHeight={1.2}>
            {isEdit
              ? isRTL
                ? 'تعديل أصل ثابت'
                : 'Edit Fixed Asset'
              : isRTL
              ? 'أصل ثابت جديد'
              : 'New Fixed Asset'}
          </Typography>
          {isEdit && editAsset && (
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              {editAsset.assetCode} — {isRTL ? editAsset.assetNameAr : editAsset.assetName}
            </Typography>
          )}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3, bgcolor: isDark ? theme.palette.background.default : '#f5f7fa', overflowY: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loadingMeta && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {/* ── Section 1: Basic Info ─────────────────────────────────────── */}
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <BusinessIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" fontWeight="bold" color="primary">
              {isRTL ? 'المعلومات الأساسية' : 'Basic Information'}
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Row 1: Names */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 45%', minWidth: 220 }}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  label={isRTL ? 'اسم الأصل (إنجليزي)' : 'Asset Name (EN)'}
                  value={form.assetName}
                  onChange={e => set('assetName', e.target.value)}
                />
              </Box>
              <Box sx={{ flex: '1 1 45%', minWidth: 220 }}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  label={isRTL ? 'اسم الأصل (عربي)' : 'Asset Name (AR)'}
                  value={form.assetNameAr}
                  onChange={e => set('assetNameAr', e.target.value)}
                  inputProps={{ dir: 'rtl' }}
                />
              </Box>
            </Box>

            {/* Row 2: Type, Category, Branch */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 28%', minWidth: 170 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>{isRTL ? 'نوع الأصل' : 'Asset Type'}</InputLabel>
                  <Select
                    value={form.assetType}
                    label={isRTL ? 'نوع الأصل' : 'Asset Type'}
                    onChange={e => set('assetType', e.target.value as AssetType)}
                  >
                    <MenuItem value="Tangible">
                      {isRTL ? 'ملموس' : 'Tangible'}
                    </MenuItem>
                    <MenuItem value="Intangible">
                      {isRTL ? 'غير ملموس' : 'Intangible'}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: '1 1 32%', minWidth: 190 }}>
                <Autocomplete
                  size="small"
                  options={categories}
                  getOptionLabel={(cat) => getCategoryLabel(cat)}
                  value={categories.find(c => String(c.categoryId) === form.categoryId) || null}
                  onChange={(_, v) => set('categoryId', v ? String(v.categoryId) : '')}
                  isOptionEqualToValue={(o, v) => o.categoryId === v.categoryId}
                  renderInput={(params) => (
                    <TextField {...params} label={isRTL ? 'الفئة' : 'Category'} />
                  )}
                />
              </Box>
              <Box sx={{ flex: '1 1 32%', minWidth: 190 }}>
                <Autocomplete
                  size="small"
                  options={branches}
                  getOptionLabel={(b) => getBranchLabel(b)}
                  value={branches.find(b => String(b.branchId) === form.branchId) || null}
                  onChange={(_, v) => set('branchId', v ? String(v.branchId) : '')}
                  isOptionEqualToValue={(o, v) => o.branchId === v.branchId}
                  renderInput={(params) => (
                    <TextField {...params} label={isRTL ? 'الفرع' : 'Branch'} />
                  )}
                />
              </Box>
            </Box>

            {/* Row 3: Notes */}
            <Box>
              <TextField
                fullWidth
                multiline
                rows={2}
                size="small"
                label={isRTL ? 'ملاحظات' : 'Notes'}
                value={form.notes}
                onChange={e => set('notes', e.target.value)}
              />
            </Box>
          </Box>
        </Paper>

        {/* ── Section 2: Acquisition ────────────────────────────────────── */}
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AccountBalanceIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" fontWeight="bold" color="primary">
              {isRTL ? 'بيانات الاستحواذ' : 'Acquisition Details'}
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 28%', minWidth: 180 }}>
              <TextField
                fullWidth
                required
                size="small"
                type="date"
                label={isRTL ? 'تاريخ الاستحواذ' : 'Acquisition Date'}
                value={form.acquisitionDate}
                onChange={e => set('acquisitionDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box sx={{ flex: '1 1 32%', minWidth: 200 }}>
              <TextField
                fullWidth
                required
                size="small"
                type="number"
                label={isRTL ? 'تكلفة الاستحواذ (د.ك)' : 'Acquisition Cost (KWD)'}
                value={form.acquisitionCost}
                onChange={e => set('acquisitionCost', e.target.value)}
                inputProps={{ min: 0, step: 0.001 }}
              />
            </Box>
            <Box sx={{ flex: '1 1 32%', minWidth: 200 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label={isRTL ? 'القيمة المتبقية (د.ك)' : 'Residual Value (KWD)'}
                value={form.residualValue}
                onChange={e => set('residualValue', e.target.value)}
                inputProps={{ min: 0, step: 0.001 }}
              />
            </Box>
          </Box>
        </Paper>

        {/* ── Section 3: Depreciation Setup ────────────────────────────── */}
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <TrendingDownIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" fontWeight="bold" color="primary">
              {isRTL ? 'إعداد الاستهلاك' : 'Depreciation Setup'}
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Row 1: Life, Method, Rate */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 28%', minWidth: 170 }}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  type="number"
                  label={isRTL ? 'العمر الإنتاجي (سنوات)' : 'Useful Life (Years)'}
                  value={form.usefulLifeYears}
                  onChange={e => set('usefulLifeYears', e.target.value)}
                  inputProps={{ min: 1, step: 1 }}
                />
              </Box>
              <Box sx={{ flex: '1 1 36%', minWidth: 210 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>{isRTL ? 'طريقة الاستهلاك' : 'Depreciation Method'}</InputLabel>
                  <Select
                    value={form.depreciationMethod}
                    label={isRTL ? 'طريقة الاستهلاك' : 'Depreciation Method'}
                    onChange={e =>
                      set('depreciationMethod', e.target.value as DepreciationMethod)
                    }
                  >
                    <MenuItem value="StraightLine">
                      {isRTL
                        ? 'القسط الثابت'
                        : 'Straight Line (القسط الثابت)'}
                    </MenuItem>
                    <MenuItem value="DecliningBalance">
                      {isRTL
                        ? 'القسط المتناقص'
                        : 'Declining Balance (القسط المتناقص)'}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
              {form.depreciationMethod === 'DecliningBalance' && (
                <Box sx={{ flex: '1 1 28%', minWidth: 170 }}>
                  <TextField
                    fullWidth
                    required
                    size="small"
                    type="number"
                    label={isRTL ? 'نسبة الاستهلاك %' : 'Declining Rate %'}
                    value={form.decliningRate}
                    onChange={e => set('decliningRate', e.target.value)}
                    inputProps={{ min: 0.01, max: 100, step: 0.01 }}
                    helperText={isRTL ? 'أدخل 40 للدلالة على 40%' : 'Enter 40 for 40%'}
                  />
                </Box>
              )}
            </Box>

            {/* Row 2: Account selectors */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 30%', minWidth: 200 }}>
                <AccountSelector
                  value={form.assetAccountId}
                  onChange={id => set('assetAccountId', id)}
                  label={isRTL ? 'حساب الأصل' : 'Asset Account'}
                  fullWidth
                  onlyFinalAccounts={true}
                />
              </Box>
              <Box sx={{ flex: '1 1 30%', minWidth: 200 }}>
                <AccountSelector
                  value={form.accumDepAccountId}
                  onChange={id => set('accumDepAccountId', id)}
                  label={isRTL ? 'حساب مجمع الاستهلاك' : 'Accum. Depreciation Account'}
                  fullWidth
                  onlyFinalAccounts={true}
                />
              </Box>
              <Box sx={{ flex: '1 1 30%', minWidth: 200 }}>
                <AccountSelector
                  value={form.depExpenseAccountId}
                  onChange={id => set('depExpenseAccountId', id)}
                  label={isRTL ? 'حساب مصروف الاستهلاك' : 'Depreciation Expense Account'}
                  fullWidth
                  onlyFinalAccounts={true}
                />
              </Box>
            </Box>

            {/* Row 3: Cost Center */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '0 1 320px', minWidth: 200 }}>
                <Autocomplete
                  size="small"
                  options={costCenters}
                  getOptionLabel={(cc) => `${cc.costCenterCode} — ${isRTL ? cc.costCenterNameAr : cc.costCenterName}`}
                  value={costCenters.find(cc => String(cc.costCenterId) === form.costCenterId) || null}
                  onChange={(_, v) => set('costCenterId', v ? String(v.costCenterId) : '')}
                  isOptionEqualToValue={(o, v) => o.costCenterId === v.costCenterId}
                  renderInput={(params) => (
                    <TextField {...params} label={isRTL ? 'مركز التكلفة' : 'Cost Center'} />
                  )}
                />
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* ── Depreciation Preview ──────────────────────────────────────── */}
        {preview && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: isDark ? alpha(theme.palette.primary.main, 0.12) : '#e8f4fd',
              border: `1px solid ${isDark ? alpha(theme.palette.primary.main, 0.3) : '#90caf9'}`,
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              color="primary"
              sx={{ mb: 1.5 }}
            >
              {isRTL ? 'معاينة الاستهلاك' : 'Depreciation Preview'}{' — '}
              <Box component="span" sx={{ fontWeight: 'normal' }}>
                {preview.label}
              </Box>
            </Typography>
            <Box sx={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {form.depreciationMethod === 'DecliningBalance'
                    ? isRTL
                      ? 'قسط السنة الأولى (سنوي)'
                      : 'Year 1 Annual'
                    : isRTL
                    ? 'القسط السنوي'
                    : 'Annual'}
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="primary.dark">
                  {preview.annual.toFixed(3)}{' '}
                  <Typography component="span" variant="caption" color="text.secondary">
                    KWD
                  </Typography>
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {form.depreciationMethod === 'DecliningBalance'
                    ? isRTL
                      ? 'قسط السنة الأولى (شهري)'
                      : 'Year 1 Monthly'
                    : isRTL
                    ? 'القسط الشهري'
                    : 'Monthly'}
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="primary.dark">
                  {preview.monthly.toFixed(3)}{' '}
                  <Typography component="span" variant="caption" color="text.secondary">
                    KWD
                  </Typography>
                </Typography>
              </Box>
              {form.depreciationMethod === 'StraightLine' && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {isRTL ? 'القيمة المتبقية عند نهاية العمر' : 'Book Value at End'}
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="text.secondary">
                    {parseFloat(form.residualValue || '0').toFixed(3)}{' '}
                    <Typography component="span" variant="caption">
                      KWD
                    </Typography>
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        )}
      </DialogContent>

      {/* ── Actions ──────────────────────────────────────────────────────── */}
      <DialogActions sx={{ px: 3, py: 2, bgcolor: isDark ? theme.palette.background.default : '#f5f7fa', gap: 1 }}>
        <Button onClick={onClose} disabled={loading} color="inherit">
          {isRTL ? 'إلغاء' : 'Cancel'}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          size="large"
          sx={{
            background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
            minWidth: 120,
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} color="inherit" />
              <span>{isRTL ? 'جاري الحفظ...' : 'Saving...'}</span>
            </Box>
          ) : isEdit ? (
            isRTL ? 'تحديث' : 'Update'
          ) : (
            isRTL ? 'حفظ' : 'Save'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FixedAssetDialog;
